import type {ReactElement} from 'react';
import React from 'react';
import styles from './social-media-progress-bar.module.scss';

export enum SocialMediaProgressBarSize {
  XSMALL = 'XSMALL',
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

interface SocialMediaProgressBarProps {
  currentStep: number;
  newStep: number;
  labels: string[];
  className?: string;
  size?: SocialMediaProgressBarSize;
}

export function SocialMediaProgressBar({currentStep, newStep, labels, className = '', size = SocialMediaProgressBarSize.LARGE}: SocialMediaProgressBarProps): ReactElement<any> | null {
  const animatableElements: ReactElement<any>[] = [];
  const isBackwards: boolean = newStep < currentStep;

  const isFirstStep = (index: number): boolean => {
    return index === 0;
  };

  const isLastStep = (index: number): boolean => {
    return index + 1 === labels.length;
  };

  const getNextToAnimate = (eventTarget: HTMLElement): Element | null => {
    let nextToAnimate: Element | null = null;
    const parentEventTarget: Element | null = eventTarget.parentElement;

    if (parentEventTarget) {
      if (parentEventTarget.classList.contains('js-label-parent')) {
        nextToAnimate = isBackwards ? parentEventTarget.previousElementSibling : parentEventTarget.nextElementSibling;
      } else {
        const sibling: Element | null = isBackwards ? eventTarget.previousElementSibling : eventTarget.nextElementSibling;

        if (sibling) {
          nextToAnimate = sibling.firstElementChild;
        }
      }
    }

    return nextToAnimate;
  };

  const onAnimationEndBackwards = (eventTarget: HTMLElement): void => {
    if (eventTarget) {
      const nextToAnimate: Element | null = getNextToAnimate(eventTarget);
      const parentEventTarget: Element | null = eventTarget.parentElement;

      if (nextToAnimate) {
        const elementStepNum = nextToAnimate.getAttribute('data-step');
        if (eventTarget.classList.contains('js-progress-circle') && parentEventTarget) {
          eventTarget.classList?.add(styles.circle);
        }

        if ((elementStepNum && parseInt(elementStepNum, 10) !== newStep) || nextToAnimate.classList.contains('js-progress-bar')) {
          if (nextToAnimate.classList.contains('js-progress-circle')) {
            const labelElement: Element | null = nextToAnimate.nextElementSibling;
            labelElement?.classList.remove('body-xxs-bold');
            labelElement?.classList.add('body-xxs');
          }

          nextToAnimate.classList?.remove(styles.complete);
          nextToAnimate.classList?.add(styles.animateBack);
        }
      }
    }
  };

  const onAnimationEndForwards = (eventTarget: HTMLElement): void => {
    if (eventTarget) {
      const nextToAnimate: Element | null = getNextToAnimate(eventTarget);

      if (nextToAnimate) {
        const elementStepNum = nextToAnimate.getAttribute('data-step');

        if ((elementStepNum && parseInt(elementStepNum, 10) !== newStep) || nextToAnimate.classList.contains('js-progress-circle')) {
          if (nextToAnimate.classList.contains('js-progress-circle')) {
            const labelElement: Element | null = nextToAnimate.nextElementSibling;
            labelElement?.classList.add('body-xxs-bold');
          }
          nextToAnimate.classList?.add(styles.animated);
        }
      }
    }
  };

  const getCircleCompleteClasses = (index: number): string => {
    if (isCircleAlreadyFilled(index)) {
      if (!isBackwards) {
        return `${styles.animated} ${styles.complete}`;
      }

      if (index + 1 === currentStep) {
        return `${styles.animateBack}`;
      }

      return `${styles.complete}`;
    }

    return '';
  };

  const isCircleAlreadyFilled = (index: number): boolean => {
    return currentStep >= index + 1;
  };

  const getBarCompleteClasses = (index: number): string => {
    if (isBarAlreadyFilled(index)) {
      if (!isBackwards) {
        return `${styles.animated} ${styles.complete}`;
      }

      return `${styles.complete}`;
    }

    return '';
  };

  const isBarAlreadyFilled = (index: number): boolean => {
    return currentStep > index + 1;
  };

  const populateElements = (): void => {
    labels.forEach((label, index) => {
      animatableElements.push(getStep(index, label));

      if (!isLastStep(index)) {
        animatableElements.push(getBar(index));
      }
    });
  };

  const isLabelBold = (index: number): boolean => {
    const forwardAnimationBold = !isBackwards && index + 1 <= currentStep;
    const backwardAnimationBold = isBackwards && index + 1 < currentStep;
    return forwardAnimationBold || backwardAnimationBold;
  };

  const getLabelTextClasses = (index: number): string => {
    const isBold = isLabelBold(index);
    if (isBold) {
      return `body-xxs-bold`;
    }

    return `body-xxs`;
  };

  const getStep = (index: number, label: string): ReactElement<any> => {
    const isFirstOrLast = isLastStep(index) || isFirstStep(index);
    return (
      <div
        key={`${index}-step`}
        className={`flex-column-align-center js-label-parent ${styles.labelParent}  ${!isFirstStep(index) ? styles.intermediateStep : ''} ${
          isLastStep(index) ? styles.lastStep : ''
        } `}
      >
        <div
          data-step={index + 1}
          className={`js-progress-circle ${styles.circle} ${isFirstOrLast ? styles.large : ''} ${getCircleCompleteClasses(index)} 
      `}
          onAnimationEnd={(event) => {
            isBackwards ? onAnimationEndBackwards(event.currentTarget) : onAnimationEndForwards(event.currentTarget);
          }}
        />
        <div className={`${getLabelTextClasses(index)} ${styles.labelText}`}>{label}</div>
      </div>
    );
  };

  const onAnimationEnd = (eventTarget: HTMLElement): void => {
    if (isBackwards) {
      onAnimationEndBackwards(eventTarget);
      return;
    }

    onAnimationEndForwards(eventTarget);
  };

  const getBarClassesByIndex = (index: number): string => {
    if (isFirstStep(index)) {
      return styles.firstBar;
    }

    return styles.otherBars;
  };

  const getBarClassesBySize = (): string => {
    if (size === SocialMediaProgressBarSize.XSMALL) {
      return styles.xsmall;
    }

    if (size === SocialMediaProgressBarSize.SMALL) {
      return styles.small;
    }

    return styles.large;
  };

  const getBar = (index: number): ReactElement<any> => {
    return (
      <div
        key={`${index}-bar`}
        data-step={index + 1}
        className={`js-progress-bar ${styles.bar} ${getBarClassesByIndex(index)} ${getBarClassesBySize()} ${getBarCompleteClasses(index)}`}
        onAnimationEnd={(event): void => {
          onAnimationEnd(event.currentTarget);
        }}
      />
    );
  };

  populateElements();

  return (
    <div className={`flex-v-row ${styles.progressBarContainer} ${className}`}>
      <div className={`flex-h-row ${styles.animatableElements}`}>{animatableElements}</div>
    </div>
  );
}
